import { type ProductizedServiceDescription_productizedService$key } from '#app/__generated__/relay/ProductizedServiceDescription_productizedService.graphql.js';
import { Text } from '#app/components/Primitives/Text.js';
import { RichText } from '#app/components/RichTextEditor/RichText.js';
import { useIsMobile } from '#app/hooks/useIsMobile.js';
import { CustomEditorContent, isBlocknoteContentEmpty } from '@contra/editor';
import { useIsomorphicLayoutEffect } from '@contra/react-hooks/useIsomorphicLayoutEffect';
import { graphql, useFragment } from '@contra/relay';
import { useRef, useState } from 'react';
import { css, styled } from 'styled-components';

const MAX_DESCRIPTION_HEIGHT = 200;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const OverflowToggleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const EditorWrapper = styled.div<{ showingMore: boolean }>(
  ({ showingMore }) => `
    position: relative;

    ${
      showingMore
        ? ''
        : `max-height: ${MAX_DESCRIPTION_HEIGHT}px; overflow-y: hidden;`
    }
  `,
);

const EditorOverlay = styled.div<{ showingMore: boolean }>(
  ({ showingMore }) => `
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 50%;

    ${
      showingMore
        ? 'background-color: transparent;'
        : 'background: linear-gradient(-180deg, rgba(255, 255, 255, 0) 14.15%, #FFFFFF 98.23%);'
    }
  `,
);

const Sections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  & > *:last-child {
    margin: 0 -14px;
  }
`;

const SectionHeading = styled(Text).attrs({
  textStyle: 'sixthLevelHeadline',
})``;

const Faqs = styled.ul`
  list-style: auto;
`;

const Faq = styled.li`
  ${({ theme }) => css`
    list-style-type: disc;
    display: list-item;
    margin-left: 16px;

    &::marker {
      color: ${theme.colorsV2.gray70};
    }

    &:not(:last-child) {
      margin-bottom: 16px;
    }

    > *:first-child {
      margin-bottom: 6px;
    }
  `}
`;

const FaqAnswer = styled(Text)`
  ${({ theme }) => css`
    color: ${theme.colorsV2.gray70};
  `}
`;

type ProductizedServiceDescriptionProps = {
  readonly productizedServiceRef: ProductizedServiceDescription_productizedService$key;
};

const EditorContent = ({
  blocknoteContent,
  title,
}: {
  readonly blocknoteContent: unknown;
  readonly title: string;
}) => {
  return (
    <SectionContainer>
      <SectionHeading>{title}</SectionHeading>
      <RichText content={blocknoteContent as CustomEditorContent} />
    </SectionContainer>
  );
};

export const ProductizedServiceDescription = ({
  productizedServiceRef,
}: ProductizedServiceDescriptionProps) => {
  const isMobile = useIsMobile();
  const [showOverflowToggle, setShowOverflowToggle] = useState(false);
  const [showingMore, setShowingMore] = useState(false);
  const toggleShowingMore = () => setShowingMore(!showingMore);
  const { blocknoteContent, blocknoteProcessDescription, faqs } =
    useFragment<ProductizedServiceDescription_productizedService$key>(
      graphql`
        fragment ProductizedServiceDescription_productizedService on ProductizedService {
          blocknoteContent
          blocknoteProcessDescription
          faqs {
            edges {
              node {
                id
                question
                answer
              }
            }
          }
        }
      `,
      productizedServiceRef,
    );

  const faqList = faqs.edges.map(({ node: { answer, id, question } }) => ({
    answer,
    id,
    question,
  }));

  const ref = useRef<HTMLDivElement>(null);

  const sections = (
    <Sections>
      <SectionContainer>
        <SectionHeading>Summary</SectionHeading>
        <RichText content={blocknoteContent as CustomEditorContent} />
      </SectionContainer>

      {isBlocknoteContentEmpty(
        blocknoteProcessDescription as CustomEditorContent,
      ) ? null : (
        <EditorContent
          blocknoteContent={blocknoteProcessDescription}
          title="Process"
        />
      )}

      {faqList.length > 0 ? (
        <SectionContainer>
          <SectionHeading>FAQs</SectionHeading>

          <Faqs>
            {faqList.map(({ answer, id, question }) => (
              <Faq key={id}>
                <Text textStyle="subtitleRegular">{question}</Text>
                <FaqAnswer textStyle="bodyRegular">{answer}</FaqAnswer>
              </Faq>
            ))}
          </Faqs>
        </SectionContainer>
      ) : null}
    </Sections>
  );

  useIsomorphicLayoutEffect(() => {
    if (ref.current) {
      if (ref.current.offsetHeight > MAX_DESCRIPTION_HEIGHT) {
        setShowOverflowToggle(true);
      } else {
        setShowOverflowToggle(false);
      }
    }
  }, []);

  if (isMobile) {
    return (
      <Container>
        {isMobile && <Text textStyle="overline">About this service</Text>}
        <EditorWrapper
          ref={ref}
          showingMore={!showOverflowToggle || showingMore}
        >
          {sections}
          <EditorOverlay showingMore={!showOverflowToggle || showingMore} />
        </EditorWrapper>
        {showOverflowToggle ? (
          <OverflowToggleWrapper>
            <Text
              onClick={toggleShowingMore}
              textStyle="subtitleRegular"
            >
              {showingMore ? 'Show less' : 'Show more'}
            </Text>
          </OverflowToggleWrapper>
        ) : null}
      </Container>
    );
  }

  return <Container>{sections}</Container>;
};
